import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Container, Typography, TextField, Button, Snackbar, Alert, Box, CircularProgress } from '@mui/material';
import Quill from 'quill';
import MediaUploadField from './MediaUploadField';

const fonts = ['Arial', 'Verdana', 'Century Schoolbook', 'Times New Roman', 'Sans-serif'];
const fontNames = fonts.map(font => font.toLowerCase().replace(/\s/g, '-'));

const Font = Quill.import('formats/font');
Font.whitelist = fontNames;
Quill.register(Font, true);

const modules = {
    toolbar: [
        [{ font: fontNames }],
        [{ header: '1' }, { header: '2' }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ['link', 'image', 'blockquote', 'code-block'],
        ['clean'],
    ],
};

const emptyFormData = {
    imagem: '',
    nome: '',
    instituicao: '',
    trabalhos: '',
    linkcv: '',
    descricao: '',
};

const stripHtml = (value = '') =>
  String(value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const DashboardAutores = () => {
    const apiUrl = process.env.REACT_APP_API_URL || '/api';

    const [formData, setFormData] = useState(emptyFormData);
    const [autores, setAutores] = useState([]);
    const [loadingAutores, setLoadingAutores] = useState(false);
    const [editingAuthorId, setEditingAuthorId] = useState(null);

    const [alertMessage, setAlertMessage] = useState({ type: '', text: '' });
    const [openSnackbar, setOpenSnackbar] = useState(false);

    const carregarAutores = async () => {
        try {
            setLoadingAutores(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${apiUrl}/autores`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            const data = Array.isArray(response.data) ? response.data : (response.data?.autores || []);
            setAutores(data);
        } catch {
            setAlertMessage({ type: 'error', text: 'Erro ao carregar autores.' });
            setOpenSnackbar(true);
        } finally {
            setLoadingAutores(false);
        }
    };

    useEffect(() => {
        carregarAutores();
    }, [apiUrl]);

    const handleChange = (event) => {
        setFormData({
          ...formData,
          [event.target.name]: event.target.value
        });
      };
    const setFieldValue = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };
    const handleUploadError = (message) => {
        setAlertMessage({ type: 'error', text: message });
        setOpenSnackbar(true);
    };
    const handleUploadSuccess = (message) => {
        setAlertMessage({ type: 'success', text: message });
        setOpenSnackbar(true);
    };

    const iniciarEdicao = (autor) => {
        setEditingAuthorId(autor._id || autor.id);
        setFormData({
            imagem: autor.imagem || '',
            nome: autor.nome || '',
            instituicao: autor.instituicao || '',
            trabalhos: autor.trabalhos || '',
            linkcv: autor.linkcv || '',
            descricao: autor.descricao || '',
        });
    };

    const cancelarEdicao = () => {
        setEditingAuthorId(null);
        setFormData(emptyFormData);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        if (!token) {
            setAlertMessage({ type: 'error', text: 'Você precisa estar autenticado para adicionar um autor.' });
            setOpenSnackbar(true);
            return;
        }

        const requiredFields = [
            ['Nome', formData.nome],
            ['Instituição', formData.instituicao],
            ['Foto do autor', formData.imagem],
            ['Link do CV', formData.linkcv],
            ['Trabalhos', formData.trabalhos],
            ['Descrição', formData.descricao],
        ];
        const missing = requiredFields
            .filter(([, value]) => !stripHtml(value))
            .map(([label]) => label);

        if (missing.length) {
            setAlertMessage({
                type: 'error',
                text: `Preencha os campos obrigatórios: ${missing.join(', ')}.`,
            });
            setOpenSnackbar(true);
            return;
        }

 
        try {
            const requestConfig = {
                headers: { Authorization: `Bearer ${token}` },
            };

            if (editingAuthorId) {
                await axios.put(`${apiUrl}/autores/${editingAuthorId}`, { ...formData }, requestConfig);
                setAlertMessage({ type: 'success', text: 'Autor atualizado com sucesso!' });
            } else {
                await axios.post(`${apiUrl}/autores`, { ...formData }, requestConfig);
                setAlertMessage({ type: 'success', text: 'Autor adicionado com sucesso!' });
            }

            cancelarEdicao();
            await carregarAutores();
        } catch (error) {
            const backendMessage = error?.response?.data?.error || error?.response?.data?.message;
            setAlertMessage({
                type: 'error',
                text: backendMessage || (editingAuthorId ? 'Erro ao atualizar o autor.' : 'Erro ao adicionar o autor. Tente novamente.'),
            });
        }
        setOpenSnackbar(true);
    };

    return (
        <Container maxWidth="lg" style={{ backgroundColor: "#f9f9f9", borderRadius: "12px", padding: "32px", boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)", marginTop: "32px" }}>
            <Typography variant="h2" align="center" gutterBottom style={{ fontFamily: "'Lato', serif", color: "black", borderBottom: "4px solid #d4af37", paddingBottom: "10px", textTransform: "uppercase", marginTop: "7%" }}>
                Painel de Autores
            </Typography>

            <form onSubmit={handleSubmit}>
                <Typography variant="h5" align="center" gutterBottom>
                    {editingAuthorId ? 'Editar Autor' : 'Adicionar Novo Autor'}
                </Typography>

                <TextField fullWidth label="Nome" name="nome" value={formData.nome} onChange={handleChange} required margin="normal" variant="outlined" style={{ marginBottom: "16px" }} />
                <TextField fullWidth label="Instituição" name="instituicao" value={formData.instituicao} onChange={handleChange} required margin="normal" variant="outlined" style={{ marginBottom: "16px" }} />
                <MediaUploadField
                    label="Foto do autor"
                    name="imagem"
                    value={formData.imagem}
                    onChange={setFieldValue}
                    folder="fotosautores"
                    accept="image/*"
                    required
                    onError={handleUploadError}
                    onSuccess={handleUploadSuccess}
                />
                <TextField fullWidth label="Link do CV" name="linkcv" value={formData.linkcv} onChange={handleChange} required margin="normal" variant="outlined" style={{ marginBottom: "16px" }} />
                <ReactQuill
  theme="snow"
  value={formData.trabalhos}
  onChange={(value) => setFormData({ ...formData, trabalhos: value })}
  style={{ marginBottom: "16px" }}
/>


                <Typography variant="h6" style={{ fontWeight: "bold", color: "#333", marginBottom: "8px" }}>Descrição</Typography>
                <ReactQuill value={formData.descricao} onChange={(value) => setFormData({ ...formData, descricao: value })} modules={modules} style={{ backgroundColor: "#fff", borderRadius: "8px", border: "1px solid #ddd", padding: "8px" }} />

                <Button fullWidth variant="contained" color="primary" type="submit" size="large" style={{ marginTop: "16px", padding: "12px", fontWeight: "bold", backgroundColor: "#1976d2", borderRadius: "8px", textTransform: "none" }}>
                    {editingAuthorId ? 'Salvar Alterações' : 'Enviar'}
                </Button>

                {editingAuthorId && (
                    <Button fullWidth variant="outlined" color="secondary" onClick={cancelarEdicao} size="large" style={{ marginTop: "12px", padding: "12px", fontWeight: "bold", borderRadius: "8px", textTransform: "none" }}>
                        Cancelar Edição
                    </Button>
                )}
            </form>

            <Box sx={{ mt: 5 }}>
                <Typography variant="h5" align="center" gutterBottom>
                    Autores Cadastrados
                </Typography>

                {loadingAutores ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ display: 'grid', gap: 1 }}>
                        {autores.length === 0 && (
                            <Typography variant="body2" color="text.secondary" align="center">
                                Nenhum autor carregado para edição.
                            </Typography>
                        )}
                        {autores.map((autor) => {
                            const autorId = autor._id || autor.id;
                            return (
                                <Box
                                    key={autorId}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 1.5,
                                        borderRadius: 1,
                                        border: '1px solid #e0e0e0',
                                        backgroundColor: '#fff',
                                    }}
                                >
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                            {autor.nome}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {autor.instituicao}
                                        </Typography>
                                    </Box>
                                    <Button variant="outlined" onClick={() => iniciarEdicao(autor)} disabled={!autorId}>
                                        Editar
                                    </Button>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>

            <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={() => setOpenSnackbar(false)}>
                <Alert severity={alertMessage.type}>{alertMessage.text}</Alert>
            </Snackbar>
        </Container>
    );
};

export default DashboardAutores;
